import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, ArrowLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import Layout from '../../components/layout/Layout';
import { footballEvent, newsEvents, applicationFormUrl } from '../../data/mock';

const FootballEvent = () => {
  const relatedNews = newsEvents.slice(1, 4);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${footballEvent.images[0]})` }}
        >
          <div className="absolute inset-0 overlay-navy" />
        </div>
        <div className="container-custom relative z-10">
          <Link to="/news" className="inline-flex items-center text-white/70 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to News & Events
          </Link>
          <Badge className="bg-[#d4a855] text-[#142942] mb-4">
            {footballEvent.category}
          </Badge>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {footballEvent.title}
          </h1>
          <div className="flex flex-wrap gap-6 text-white/80">
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#d4a855]" />
              {footballEvent.date}
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#d4a855]" />
              {footballEvent.location}
            </span>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <p className="text-lg text-[#475569] leading-relaxed mb-6">
                  {footballEvent.description}
                </p>
                <div className="bg-[#f8fafc] border-l-4 border-[#d4a855] p-6 rounded-r-lg mb-8">
                  <p className="text-[#1e3a5f] font-medium italic">
                    "{footballEvent.impactLine}"
                  </p>
                </div>
              </div>

              {/* Photo Gallery */}
              <h2 className="font-display text-2xl font-bold text-[#1e3a5f] mb-6">
                Event Gallery
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {footballEvent.images.map((image, index) => (
                  <div
                    key={index}
                    className={`relative overflow-hidden rounded-lg ${
                      index === 0 ? 'md:col-span-2 h-80' : 'h-60'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${footballEvent.title} - Image ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                ))}
              </div>

              {/* Event Highlights */}
              <h2 className="font-display text-2xl font-bold text-[#1e3a5f] mb-6">
                Event Highlights
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <Card className="border-none shadow-md">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#1e3a5f] rounded-lg flex items-center justify-center mb-4">
                      <span className="text-2xl">⚽</span>
                    </div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-2">Athletic Excellence</h4>
                    <p className="text-[#475569] text-sm">
                      Showcasing student athleticism and competitive spirit through thrilling matches.
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-md">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#1e3a5f] rounded-lg flex items-center justify-center mb-4">
                      <span className="text-2xl">🤝</span>
                    </div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-2">Teamwork & Sportsmanship</h4>
                    <p className="text-[#475569] text-sm">
                      Promoting collaboration and healthy competition among students and institutions.
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-md">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#1e3a5f] rounded-lg flex items-center justify-center mb-4">
                      <span className="text-2xl">🏆</span>
                    </div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-2">Community Spirit</h4>
                    <p className="text-[#475569] text-sm">
                      Bringing together students, staff, and neighboring institutions in celebration.
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-none shadow-md">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#1e3a5f] rounded-lg flex items-center justify-center mb-4">
                      <span className="text-2xl">💪</span>
                    </div>
                    <h4 className="font-semibold text-[#1e3a5f] mb-2">Health & Fitness</h4>
                    <p className="text-[#475569] text-sm">
                      Encouraging physical activity and a healthy lifestyle among students.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                {/* Event Details Card */}
                <Card className="border-none shadow-lg mb-6">
                  <CardContent className="p-6">
                    <h3 className="font-display font-semibold text-[#1e3a5f] text-lg mb-4">
                      Event Details
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <span className="text-[#475569] text-sm">Date</span>
                        <p className="text-[#1e3a5f] font-medium">{footballEvent.date}</p>
                      </div>
                      <div>
                        <span className="text-[#475569] text-sm">Location</span>
                        <p className="text-[#1e3a5f] font-medium">{footballEvent.location}</p>
                      </div>
                      <div>
                        <span className="text-[#475569] text-sm">Category</span>
                        <p className="text-[#1e3a5f] font-medium">{footballEvent.category}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Related News */}
                <h3 className="font-display font-semibold text-[#1e3a5f] text-lg mb-4">
                  Related News
                </h3>
                <div className="space-y-4">
                  {relatedNews.map((item) => (
                    <Card key={item.id} className="border-none shadow-sm card-hover">
                      <CardContent className="p-4">
                        <span className="text-[#d4a855] text-xs font-medium">{item.date}</span>
                        <h4 className="font-semibold text-[#1e3a5f] text-sm mt-1 line-clamp-2">
                          {item.title}
                        </h4>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* CTA */}
                <Card className="mt-6 bg-[#1e3a5f] border-none">
                  <CardContent className="p-6 text-center">
                    <h4 className="font-display font-semibold text-white text-lg mb-2">
                      Join Our Community
                    </h4>
                    <p className="text-white/70 text-sm mb-4">
                      Be part of exciting events and activities at BIT.
                    </p>
                    <a href={applicationFormUrl} target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-[#d4a855] hover:bg-[#f4d794] text-[#142942]">
                        Apply Now
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FootballEvent;
